import { Injectable, inject } from '@angular/core'
import { Observable, catchError, of, tap, map } from 'rxjs'
import { BaseAsyncService } from '@core/state/base-service'
import { FileTrackingDto } from '@shared/models/dto/api-dtos'
import { FileTrackingStore } from '@core/state/file-tracking.store'

/**
 * Service for managing File Tracking data
 * Handles API calls to the /api/file-tracking endpoints
 * Provides methods to fetch all tracking steps and the current step for a succession file
 * Extends BaseAsyncService for consistent error handling and retry logic
 */
@Injectable({ providedIn: 'root' })
export class FileTrackingService extends BaseAsyncService {
  private readonly fileTrackingStore = inject(FileTrackingStore)

  private readonly FILE_TRACKING_ENDPOINT = '/file-tracking'

  /**
   * Loads all tracking steps for a succession file
   * Returns all 6 steps (GenealogistEntry, FileCreated, ResearchInProgress,
   * NoNewHeirsFound, ResearchValidated, DeliveryImminent) with their states:
   * - Pending: not yet reached
   * - Active: currently in progress
   * - Completed: finished
   *
   * @param successionFileId The ID of the succession file
   * @returns Observable of FileTrackingDto array containing all steps with their states
   */
  loadFileTrackingSteps(successionFileId: number): Observable<FileTrackingDto[]> {
    this.fileTrackingStore.setLoading(true)
    this.fileTrackingStore.setError(null)

    const endpoint = `${this.FILE_TRACKING_ENDPOINT}/succession-file/${successionFileId}`

    return this.get<FileTrackingDto[]>(endpoint, {
      logErrors: true,
      retry: this.createRetryConfig(0, 800, 1.5),
    }).pipe(
      map((steps: FileTrackingDto[]) => {
        // Update store with all steps data
        this.fileTrackingStore.setFileTrackingSteps(steps)
        this.fileTrackingStore.setSuccessionFileId(successionFileId)

        return steps
      }),
      catchError(error => {
        this.fileTrackingStore.setError('Impossible de charger le suivi du dossier')
        console.error('Error loading file tracking steps:', error)
        return of([])
      }),
      tap(() => this.fileTrackingStore.setLoading(false))
    )
  }

  /**
   * Loads the current tracking step for a succession file
   * Returns the latest tracking record (current step)
   *
   * @param successionFileId The ID of the succession file
   * @returns Observable of FileTrackingDto representing the current step
   */
  loadCurrentFileTrackingStep(successionFileId: number): Observable<FileTrackingDto | null> {
    this.fileTrackingStore.setLoading(true)
    this.fileTrackingStore.setError(null)

    const endpoint = `${this.FILE_TRACKING_ENDPOINT}/succession-file/${successionFileId}/current`

    return this.get<FileTrackingDto>(endpoint, {
      logErrors: true,
      retry: this.createRetryConfig(0, 800, 1.5),
    }).pipe(
      map((currentStep: FileTrackingDto) => {
        // Update store with current step data
        this.fileTrackingStore.setCurrentStep(currentStep)
        this.fileTrackingStore.setSuccessionFileId(successionFileId)

        return currentStep
      }),
      catchError(error => {
        this.fileTrackingStore.setError("Impossible de charger l'étape courante du dossier")
        console.error('Error loading current file tracking step:', error)
        return of(null)
      }),
      tap(() => this.fileTrackingStore.setLoading(false))
    )
  }

  /**
   * Loads both all steps and current step for a succession file
   * Convenience method to fetch complete tracking information
   *
   * @param successionFileId The ID of the succession file
   * @returns Observable of object containing both all steps and current step
   */
  loadFileTracking(successionFileId: number): Observable<{
    steps: FileTrackingDto[]
    currentStep: FileTrackingDto | null
  }> {
    return new Observable(observer => {
      let steps: FileTrackingDto[] = []
      const currentStep: FileTrackingDto | null = null
      let stepsLoaded = false
      const currentStepLoaded = false

      const checkComplete = () => {
        if (stepsLoaded && currentStepLoaded) {
          observer.next({ steps, currentStep })
          observer.complete()
        }
      }

      // Load all steps
      this.loadFileTrackingSteps(successionFileId).subscribe({
        next: data => {
          steps = data
          stepsLoaded = true
          checkComplete()
        },
        error: () => {
          stepsLoaded = true
          checkComplete()
        },
      })

      // Load current step
      // todo: figma: "Suivi du dossier" screen 16
      /*this.loadCurrentFileTrackingStep(successionFileId).subscribe({
        next: (data) => {
          currentStep = data;
          currentStepLoaded = true;
          checkComplete();
        },
        error: () => {
          currentStepLoaded = true;
          checkComplete();
        }
      });*/
    })
  }

  /**
   * Refreshes the current file tracking data
   * Uses the last loaded succession file ID from the store
   *
   * @returns Observable of refreshed tracking data
   */
  refresh(): Observable<{
    steps: FileTrackingDto[]
    currentStep: FileTrackingDto | null
  }> {
    const currentState = this.fileTrackingStore.state()
    const successionFileId = currentState.successionFileId

    if (!successionFileId) {
      console.warn('Cannot refresh: no succession file ID in store')
      return of({ steps: [], currentStep: null })
    }

    return this.loadFileTracking(successionFileId)
  }
}
